import React from 'react';
import { Check, ArrowRight, DollarSign, Users, Target } from 'lucide-react';

const HowItWorks = () => {
  const steps = [
    {
      number: "01",
      title: "Choose Your Product",
      description: "Browse our curated list of high-converting products. Filter by commission rate, conversion rate, or category to find the perfect match.",
      icon: Target,
      color: "from-emerald-500 to-teal-600"
    },
    {
      number: "02",
      title: "Get Your Affiliate Link",
      description: "Sign up for the affiliate network (takes 2 minutes), get approved, and grab your unique tracking link.",
      icon: ArrowRight,
      color: "from-teal-500 to-cyan-600"
    },
    {
      number: "03",
      title: "Start Promoting",
      description: "Share your link on social media, blogs, YouTube, email, or paid ads. Use our proven templates and strategies.",
      icon: Users,
      color: "from-cyan-500 to-blue-600"
    },
    {
      number: "04",
      title: "Earn Commissions",
      description: "Get paid when people buy through your link. Track your earnings in real-time and scale what works.",
      icon: DollarSign,
      color: "from-blue-500 to-emerald-600"
    }
  ];

  return (
    <section id="how-it-works" className="py-20 px-4 bg-white">
      <div className="container mx-auto max-w-7xl">
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            How It Works
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Start earning in 4 simple steps. No technical skills required.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {steps.map((step, index) => {
            const Icon = step.icon;
            return (
              <div key={index} className="relative">
                {/* Connector Line */}
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-16 left-full w-full h-0.5 bg-gradient-to-r from-emerald-200 to-transparent -translate-x-1/2 z-0"></div>
                )}
                
                <div className="relative z-10 text-center">
                  {/* Icon */}
                  <div className={`w-24 h-24 mx-auto mb-6 bg-gradient-to-br ${step.color} rounded-2xl flex items-center justify-center shadow-lg transform hover:scale-105 transition-transform`}>
                    <Icon className="w-12 h-12 text-white" />
                  </div>

                  {/* Step Number */}
                  <div className="text-6xl font-bold text-emerald-100 mb-2">
                    {step.number}
                  </div>

                  {/* Content */}
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {step.title}
                  </h3>
                  <p className="text-gray-600">
                    {step.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Benefits */}
        <div className="mt-16 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-8 md:p-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            Why Choose These Products?
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              "High conversion rates (6-12%)",
              "Generous commissions (30-50%)",
              "Proven sales funnels",
              "Quality products with refund guarantees",
              "Recurring commission opportunities",
              "24/7 tracking & analytics"
            ].map((benefit, index) => (
              <div key={index} className="flex items-center space-x-3">
                <div className="w-6 h-6 bg-emerald-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <Check className="w-4 h-4 text-white" />
                </div>
                <span className="text-gray-700 font-medium">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
export default HowItWorks;
